import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { MemberEditData, RoleOption, TeamOption } from '../types/memberEdit.types';

export const memberEditService = {
  async getMemberById(id: string): Promise<MemberEditData> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, email, phone, is_active, avatar_path')
      .eq('id', id)
      .single();
    if (error) throw error;

    // Get current team
    const { data: teamData } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', id)
      .limit(1)
      .maybeSingle();

    // Get current roles
    const { data: rolesData } = await supabase
      .from('user_roles')
      .select('role_code')
      .eq('user_id', id);
    
    const roleCodes = rolesData?.map((r: any) => r.role_code) || [];

    return {
      id: data.id,
      fullName: data.full_name,
      email: data.email,
      phone: data.phone || '',
      roleCodes,
      status: data.is_active === false ? 'locked' : 'active',
      avatarPath: data.avatar_path,
      teamId: teamData?.team_id || ''
    };
  },

  async getRoles(): Promise<RoleOption[]> {
    const { data, error } = await supabase
      .from('roles')
      .select('code, name')
      .order('name');
    if (error) throw error;
    return data || [];
  },

  async getTeams(companyId?: string): Promise<TeamOption[]> {
    let query = supabase
      .from('teams')
      .select('id, name')
      .order('name');
    
    if (companyId) {
      query = query.eq('company_id', companyId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async updateMember(id: string, data: Partial<MemberEditData>): Promise<void> {
    await enforceSettingsPermission('settings.members.update', { ownerUserId: id });
    const updateData: any = {
      full_name: data.fullName,
      email: data.email,
      phone: data.phone,
      avatar_path: data.avatarPath
    };
    if (data.status !== undefined) {
      updateData.is_active = data.status === 'active';
    }
    const { error } = await supabase
      .from('user_profiles')
      .update(updateData)
      .eq('id', id);
    if (error) throw error;

    // Update roles
    if (data.roleCodes !== undefined) {
      await supabase.from('user_roles').delete().eq('user_id', id);
      if (data.roleCodes && data.roleCodes.length > 0) {
        const roleInserts = data.roleCodes.map(code => ({
          user_id: id,
          role_code: code
        }));
        await supabase.from('user_roles').insert(roleInserts);
      }
    }

    // Update team
    if (data.teamId !== undefined) {
      if (data.teamId) {
        // Upsert team
        const { error: teamError } = await supabase
          .from('team_members')
          .upsert({
            user_id: id,
            team_id: data.teamId,
            member_role: 'member'
          }, { onConflict: 'user_id, team_id' }); // Upsert by unique constraints if exists, but typically we might need to delete old and insert new. Let's delete existing first.
        
        // Simpler approach: delete existing assignments for this user and insert new
        await supabase.from('team_members').delete().eq('user_id', id);
        await supabase.from('team_members').insert({
          user_id: id,
          team_id: data.teamId,
          member_role: 'member'
        });
      } else {
        // Remove from team
        await supabase.from('team_members').delete().eq('user_id', id);
      }
    }
  }
};
